/*
  Ход загрузки файла - один на все шаги загрузки (19.10.2026).

  Указание владельца: файл загружается и разбирается ОДИН раз, процесс и его
  финал видны, а не «то ли работает, то ли завис». Выбранный вариант макета -
  А, «этапы списком с галочками»: отправка файла, разбор, проверка строк, запись.

  Что делает хук:
  1. `choose(file)` - отправляет файл с ходом в процентах и следит за его
     чтением на сервере («лист K из L, строка N из M»), опрашивая раз в секунду;
  2. `stored` - прочитанный файл; его и передают дальше в предпросмотры и
     записи вместо самого файла (`api/client.js`, `appendFile`);
  3. `check(fn)` - предпросмотр или выбор счетов: этап «Проверка строк» со
     временем и итогом словами;
  4. `write(action, params)` - запись фоновой работой с ходом «записано N из M».

  Хук знает про сервер и состояние, разметка - в `UploadStages.jsx`.
*/

import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '../../api/client.js'

const POLL = 1000
// Сколько опросов подряд можно потерять по сети, прежде чем сказать «связь оборвалась».
const NETWORK_RETRIES = 20

const pause = (ms) => new Promise((done) => setTimeout(done, ms))

const IDLE = {
  phase: 'idle',
  sent: 0,
  file: null,
  error: null,
  startedAt: null,
  sentAt: null,
  readAt: null,
  check: null,
  write: null,
}

// Запись кончилась отказом - тем же видом ошибки, что у обычного запроса.
function failure(job) {
  const error = new Error(job.error_message ?? 'Запись не выполнена.')
  error.message = job.error_message ?? 'Запись не выполнена.'
  error.remedy = job.error_remedy ?? 'Нажмите «Загрузить» ещё раз.'
  return error
}

// Связь оборвалась, пока шла запись: она могла дойти до конца, и человеку так и сказано.
function lostConnection() {
  const error = new Error('Связь с сервером оборвалась, пока шла запись.')
  error.remedy =
    'Запись могла завершиться. Нажмите «Загрузить» ещё раз: система продолжит следить за той же ' +
    'записью, а не начнёт новую. Итог виден и в журнале загрузок.'
  return error
}

export default function useUploadFlow(kind) {
  const [state, setState] = useState(IDLE)
  // Сколько раз файл не принят или не прочитан: меняет ключ поля выбора файла (см. ниже).
  const [attempt, setAttempt] = useState(0)
  const alive = useRef(true)
  // Номер текущего файла: ответ опроса прежнего файла в новое состояние не пишется.
  const current = useRef(null)
  // Запись, о которой оборвалась связь, и что именно писалось (см. `followJob`).
  const lostJob = useRef(null)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const patch = useCallback((change) => {
    if (alive.current) setState((was) => ({ ...was, ...change }))
  }, [])
  // Любой отказ - сменить ключ поля выбора файла (ревью 30.10.2026: прежде только отказ
  // отправки и чтения, а после отказа проверки или записи тот же файл не выбирался).
  const refused = useCallback(() => {
    if (alive.current) setAttempt((count) => count + 1)
  }, [])

  const watchReading = useCallback(
    async (id) => {
      while (alive.current && current.current === id) {
        const row = await api.importFile(id)
        if (current.current !== id) return
        patch({ file: row })
        if (row.state === 'read') {
          patch({ phase: 'read', readAt: Date.now() })
          return
        }
        if (row.state === 'failed') {
          patch({ phase: 'failed', error: { message: row.error_message, remedy: row.error_remedy } })
          refused()
          return
        }
        await pause(POLL)
      }
    },
    [patch, refused],
  )

  const choose = useCallback(
    async (file) => {
      current.current = null
      // Новый файл - новая запись: прежняя потерянная к нему отношения не имеет.
      lostJob.current = null
      setState({ ...IDLE, phase: file ? 'sending' : 'idle', startedAt: file ? Date.now() : null })
      if (!file) return
      try {
        const row = await api.sendImportFile(kind, file, (sent) => patch({ sent }))
        current.current = row.id
        patch({ phase: 'reading', file: row, sent: 1, sentAt: Date.now() })
        await watchReading(row.id)
      } catch (error) {
        patch({ phase: 'failed', error })
        refused()
      }
    },
    [kind, patch, watchReading, refused],
  )

  // Проверка строк - предпросмотр или выбор счетов; `fact` - итог словами.
  const check = useCallback(
    async (run, factOf) => {
      const at = Date.now()
      patch({ check: { state: 'now', at } })
      try {
        const result = await run()
        patch({
          check: { state: 'done', seconds: (Date.now() - at) / 1000, fact: factOf?.(result) },
        })
        return result
      } catch (error) {
        patch({ check: { state: 'fail', seconds: (Date.now() - at) / 1000 } })
        refused()
        throw error
      }
    },
    [patch, refused],
  )

  /*
    Запись идёт один раз за файл (аудит 26.10.2026, причина 4): повторный вызов, пока
    первая запись в пути, получает тот же ответ, а не запускает вторую работу. Кнопки
    экранов гасятся состоянием, но оно обновляется после отрисовки, и быстрое второе
    нажатие давало повторную запись того же файла.
  */
  const writing = useRef(null)
  /*
    Запись, о которой связь оборвалась (аудит 28.10.2026, раздел 5.3). Опрос хода записи падал
    по сети, этап «Запись в портфель» крутился вечно, а кнопка снова становилась активной:
    нажатие запускало вторую запись того же файла. Теперь короткий обрыв переждан, а долгий
    называется словами, и следующее «Загрузить» продолжает следить за ТОЙ ЖЕ записью.

    Ревью 30.10.2026: потерянная запись помнится вместе с тем, ЧТО писалось, - файлом,
    действием и параметрами. Прежде помнился только номер работы, и запись исправленного
    файла после обрыва отдавала итог прежнего («загружено» с его цифрами). Обрывом считается
    только сеть и сбой сервера (5xx): отказ по существу (404 «записи нет», 403) называется
    словами сервера, иначе каждое «Загрузить» снова следило бы за несуществующей работой.
  */
  const followJob = useCallback(
    async (first, at, request) => {
      let job = first
      let misses = 0
      while (job.state === 'queued' || job.state === 'running') {
        await pause(POLL)
        try {
          job = await api.importJob(job.id)
          misses = 0
        } catch (error) {
          misses += 1
          const lost = error.code === 'network' || error.status >= 500
          if (error.code === 'network' && misses <= NETWORK_RETRIES) continue
          patch({ write: { state: 'fail', at, seconds: (Date.now() - at) / 1000 } })
          refused()
          if (!lost) throw error
          lostJob.current = { ...request, id: job.id }
          throw lostConnection()
        }
        patch({ write: { state: 'now', at, done: job.done, total: job.total, stage: job.stage } })
      }
      return job
    },
    [patch, refused],
  )
  const writeOnce = useCallback(
    async (action, params = {}, second = null) => {
      const at = Date.now()
      patch({ write: { state: 'now', at, done: 0, total: null, stage: null } })
      const request = {
        file: state.file?.id ?? null,
        second: second?.storedId ?? null,
        what: JSON.stringify([action, params]),
      }
      const lost = lostJob.current
      lostJob.current = null
      let job
      if (lost && lost.file === request.file && lost.second === request.second && lost.what === request.what) {
        job = { id: lost.id, state: 'running', done: 0, total: null, stage: null }
      } else {
        try {
          job = await api.startImportJob({
            action,
            file_id: request.file,
            second_file_id: request.second,
            params,
          })
        } catch (error) {
          patch({ write: { state: 'fail', at, seconds: (Date.now() - at) / 1000 } })
          refused()
          throw error
        }
      }
      job = await followJob(job, at, request)
      const seconds = (Date.now() - at) / 1000
      if (job.state === 'failed') {
        patch({ write: { state: 'fail', at, seconds } })
        refused()
        throw failure(job)
      }
      patch({ write: { state: 'done', at, seconds, done: job.done, total: job.total } })
      return job.result
    },
    [patch, state.file, followJob, refused],
  )

  const write = useCallback(
    (action, params, second) => {
      if (writing.current) return writing.current
      const running = writeOnce(action, params, second).finally(() => {
        writing.current = null
      })
      writing.current = running
      return running
    },
    [writeOnce],
  )

  const stored =
    state.phase === 'read' && state.file
      ? { storedId: state.file.id, name: state.file.filename, size: state.file.size_bytes }
      : null

  /*
    Этапы проверки и записи отдаются как `checked` и `written`, а не под своими
    именами в состоянии (20.10.2026): одноимённые функции `check` и `write` ниже
    затирали их, и «Ход загрузки» показывал «Запись в портфель - записано» сразу
    после разбора, а «Проверку строк» - без состояния (найдено осмотром в
    браузере; проверки шагов на этапы не смотрели).
  */
  const { check: checked, write: written, ...rest } = state
  return {
    ...rest,
    checked,
    written,
    stored,
    /* Ключ поля выбора файла: после отказа поле пересоздаётся, и тот же исправленный файл
       выбрать можно (аудит 28.10.2026, раздел 5.3). Браузер не сообщает о выборе того же пути. */
    attempt,
    busy: state.phase === 'sending' || state.phase === 'reading',
    choose,
    check,
    write,
  }
}
